import React from "react"
import { useDispatch, useSelector } from "react-redux";
import { StyledModalBody } from "../../../../../components/Modals";
import { ButtonBodyVariant } from "../../../../../components/ui/ButtonVariant";
import { useTranslation } from "react-i18next";
import { closeActiveModal, openModal } from "../../../../../helper/modals";
import { Flex } from "../../../../../components/ui/Flex";
import { Palette } from "../../../../../components/utils";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, StyledFormLabel } from "../../../../../components/ui/Form";
import { useForm } from "react-hook-form";
import { ModalButtonsContainer, PaymentModal, StepItem, LightBlueText, TitleText } from "../../../PaymentComponents";
import * as Yup from 'yup';
import { yupResolver } from "@hookform/resolvers/yup";
import { addPaymentMethod, createResidentVopayAccount, updatePaymentData } from "../../../../../redux/actions/payments";
import { PAYMENT_METHOD_TYPE_BANK } from "../../../../../redux/constants";
import { SettingsModalInput } from "../../../../settings/SettingsModals";
import { setToast } from "../../../../../redux/actions/window";
import { DATE_FORMATS } from "../../../../users/constant";
import { CustomDatePicker } from "../../../../../components/CustomSearchBar";
import moment from "moment";

const schema = Yup.object().shape({
  dateOfBirth: Yup.string()
    .required('Date of birth is required'),
  lastFourDigitsOfSin: Yup.number('Only 4 digits are allowed')
    .required('Last 4 digits of SIN is required'),
  accountNumber: Yup.string()
    .required('An account number is required'),
  bankTransitNumber: Yup.string()
    .required('A bank transit number is required')
});

const SetupBankingDetailsStep2 = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const modal = useSelector((state) => state.headerModal.modal);
  const addPayment = useSelector((state) => state.addPayment);

  console.log('addPayment', addPayment)
  const form = useForm({
    resolver: yupResolver(schema),
    mode: 'onChange',
  });

  const onSubmit = (values) => {
    dispatch(updatePaymentData(values))
    dispatch(createResidentVopayAccount({
      body: {
        BuildingID: addPayment.selectedBuilding.id,
        UserID: addPayment.userId.value,
        DOB: moment(values.dateOfBirth).format(DATE_FORMATS.YYYY_MM_DD),
        SINLastDigits: values.lastFourDigitsOfSin.toString(),
      },
      success: () => {
        dispatch(addPaymentMethod({
          body: {
            BuildingID: addPayment.selectedBuilding.id,
            UserID: addPayment.userId.value,
            ForAccount: "Resident",
            BankInstitutionID: addPayment.institutionNumber,
            FinancialInstitutionNumber: addPayment.institutionNumber,
            BranchTransitNumber: values.bankTransitNumber,
            AccountNumber: values.accountNumber,
            paymentMethodType: PAYMENT_METHOD_TYPE_BANK
          },
          success: () => {
            openModal('AccountSuccessSetupModal')
          },
          fail: (err) => {
            dispatch(
              setToast({
                toastShow: true,
                toastMessage: t('Could not add payment method to Vopay account: {{error}}', { error: err?.data?.message }),
                modal: 'errorToast'
              })
            );
          }
        }))
      },
      fail: (err) => {
        dispatch(
          setToast({
            toastShow: true,
            toastMessage: t('Could not add payment method to Vopay account: {{error}}', { error: err?.data?.message }),
            modal: 'errorToast'
          })
        );
      }
    }))
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <PaymentModal
          show={modal === 'SetupBankingDetailsStep2'}
          onClose={closeActiveModal}
          closeOnBackdrop={false}
          width={'460px'}>
          <StyledModalBody>
            <Flex direction="column" gap="1rem">
              <Flex direction="column">
                <Flex justify="space-between" alignItems="center" style={{ width: "100%" }}>
                  <LightBlueText> STEP 2/2</LightBlueText>
                  <Flex gap="0.5rem">
                    <StepItem filled />
                    <StepItem filled />
                  </Flex>
                </Flex>
                <TitleText>Rent Amount</TitleText>
              </Flex>

              <Flex direction="column" alignItems="center" gap="1rem">
                <FormField
                  control={form.control}
                  name="dateOfBirth"
                  render={({ field }) => (
                    <FormItem alt>
                      <FormLabel v3>{t('Date of birth')}</FormLabel>
                      <FormControl>
                        <CustomDatePicker
                          {...field}
                          className="form-control"
                          width={'100%'}
                          height={'3rem'}
                          showSecond={false}
                          format={DATE_FORMATS.YYYY_MM_DD}
                          placeholder={t('Date of birth')}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="lastFourDigitsOfSin"
                  render={({ field }) => (
                    <FormItem alt>
                      <FormLabel v3>{t('Last 4 digits of SIN')}</FormLabel>
                      <FormControl>
                        <SettingsModalInput
                          type="text"
                          pattern="[0-9]{4}"
                          inputMode="numeric"
                          maxLength={4}
                          placeholder={t('Last 4 digits of SIN')}
                          autoComplete="off"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="accountNumber"
                  render={({ field }) => (
                    <FormItem alt>
                      <FormLabel v3>{t('Account number')}</FormLabel>
                      <FormControl>
                        <SettingsModalInput
                          type="text"
                          placeholder={t('Account number')}
                          autoComplete="off"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="bankTransitNumber"
                  render={({ field }) => (
                    <FormItem alt>
                      <FormLabel v3>{t('Bank transit number')}</FormLabel>
                      <FormControl>
                        <SettingsModalInput
                          type="text"
                          placeholder={t('Bank transit number')}
                          autoComplete="off"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormItem alt>
                  <StyledFormLabel v3 cssOverride={`color: ${Palette.CONTENT_DISABLED};`}>{t('Institution number')}</StyledFormLabel>
                  <FormControl>
                    <SettingsModalInput
                      disabled
                      type="text"
                      autoComplete="off"
                      value={addPayment.institutionNumber}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>

              </Flex>
              <ModalButtonsContainer gapY="8px">
                <ButtonBodyVariant cssOverride="width: 8.875rem; height: 2rem;" type="button" variant="secondary" onClick={closeActiveModal}>{t('Cancel')}</ButtonBodyVariant>
                <ButtonBodyVariant cssOverride="width: 8.875rem; height: 2rem;" type="submit">{t('Next')}</ButtonBodyVariant>
              </ModalButtonsContainer>
            </Flex>
          </StyledModalBody>
        </PaymentModal>
      </form>
    </Form>
  )
}

export default SetupBankingDetailsStep2